import React, { useMemo, useState } from 'react'
import Page from '../../components/layout/page.component';
import Search from '../../components/search/search.component';
import styles from '../../index.module.css'
import { FileResponse } from '../../components/interfaces/FileResponse';
import { useSearchService } from '../../services/search.service';
import SheetItem, { AddButtonPosition } from '../../components/search/sheet-item.component';
import Loader from '../../components/loader/loader.component';
import PbButton from '../../components/button/button.component';
import FileHelper from '../../helpers/filehelper';
import { StringHelper } from '../../components/infra/stringHelper.component';
import { toast } from 'react-toastify';
import {useRepertoireService} from '../../services/repertoire.service';
import StageMode from '../../components/stage-mode/stage-mode.component';
import { isPdfItem } from '../../components/search/use-sheet-source';
import ConfirmationDialog from '../../components/dialogs/confirmation.dialog';

export default function MainSearch() {
  const [foundItems, setFoundItems] = useState<FileResponse[]>();
  const [searching, setSearching] = useState(false);
  const [currentTerm, setCurrentTerm] = useState('');
  const [viewerIndex, setViewerIndex] = useState<number>();
  const [reportingItem, setReportingItem] = useState<FileResponse>();

  // The full screen viewer only pages through images; PDFs keep downloading on click
  const imageItems = useMemo(() => (foundItems || []).filter(i => !isPdfItem(i)), [foundItems]);

  // Touch devices (phones, tablets) open the image full screen; desktop keeps click-to-download
  const onImageClick = (file: FileResponse) => {
    if (!window.matchMedia?.('(pointer: coarse)').matches) return false;

    setViewerIndex(Math.max(imageItems.indexOf(file), 0));
    return true;
  }

  const searchService = useSearchService();
  const repertoireService = useRepertoireService();

  const performSearch = async (term: string) => {
    setSearching(true);
    setFoundItems(undefined)
    setCurrentTerm(term);

    try {
      if (!term) {
        return;
      }

      const { data } = await searchService.search(term);

      if (data && data.length > 0) {
        setFoundItems(data);
      }

    } catch (error) {
      toast.warning('Não foi possivel realizar a pesquisa!');
    }
    finally {
      setSearching(false);
    }

  }

  const onDownloadPdf = async () => {
    const { data } = await searchService.generatePdf(currentTerm);

    if (data) {
      const base64ByteArray = StringHelper.base64ToArrayBuffer(data);

      FileHelper.TryDownload(`${currentTerm}.pdf`, new Blob([base64ByteArray as any], { type: "application/pdf" }))
    }
  }

  const onAddButtonClick = async (file: FileResponse) => {
    try {
      await repertoireService.add(file);

      toast.success(`Partitura adicionada ao seu repertório!`);

    } catch {
      toast.error(`Não foi possivel adicionar a partitura ao repertório.`)
    }
  }

  const onReportConfirm = async () => {
    const file = reportingItem;
    if (!file) return;

    try {
      await searchService.report(currentTerm, file);

      // Hide the reported file right away; the bot stops returning it once an admin accepts the report
      setFoundItems(items => {
        const remaining = items?.filter(i => i !== file);
        return remaining && remaining.length > 0 ? remaining : undefined;
      });

      toast.success('Arquivo reportado com sucesso. Obrigado por sua contribuição!');
    } catch {
      toast.error('Não foi possivel reportar o arquivo.');
    }
  }

  return <Page
    eyebrow='Portal VIP'
    title='Encontre sua partitura'
    subtitle='Pesquise por música, artista ou álbum no acervo do Partitura BOT.'>
    <Search
      onSearchButtonClick={performSearch}
    />
    {searching && <Loader label='Buscando partituras...' />}
    {!foundItems && !searching && <p className={styles.empty}>
      {currentTerm ? `Nenhum resultado para "${currentTerm}".` : 'Digite um termo acima para começar.'}
    </p>}
    {foundItems && !searching && <div className={styles.resultsBar}>
      <p className={styles.resultsCount} aria-live='polite'>
        <strong>{foundItems.length}</strong> {foundItems.length === 1 ? 'partitura encontrada' : 'partituras encontradas'} para <strong>"{currentTerm}"</strong>
      </p>
      <PbButton label='Baixar PDF da pesquisa' onClick={onDownloadPdf} />
    </div>}
    {foundItems && !searching && <div className={styles.imageItem}>
      {foundItems.map(
        (value) =>
          <SheetItem
            key={value.serverPath}
            item={value}
            addButton={{ position: AddButtonPosition.Low, onClick: onAddButtonClick }}
            reportButton={{ onClick: setReportingItem }}
            shareable
            onImageClick={onImageClick} />)}
    </div>}
    {viewerIndex !== undefined && imageItems.length > 0 && <StageMode
      items={imageItems}
      startIndex={viewerIndex}
      onClose={() => setViewerIndex(undefined)} />}
    {reportingItem && <ConfirmationDialog
      title='Não é uma partitura?'
      text='O arquivo será enviado para revisão e deixará de aparecer nas pesquisas caso seja confirmado que não é uma partitura.'
      onConfirm={onReportConfirm}
      onClose={() => setReportingItem(undefined)} />}
  </Page>
}